import { type ReactNode } from 'react';
import { clsx } from 'clsx';
import { ListBox } from 'react-aria-components';
import styles from './TileList.module.css';

type Key = string | number;

type TileListItem = {
  id: Key;
  heading: string;
  supportingCopy?: string;
  imgSrc?: string;
  icon?: ReactNode;
};

type Props<T extends Key> = VariantProps<T> & {
  testid?: string;
  ariaLabel: string;
  children: ReactNode;
  className?: string;
};

type VariantProps<T extends Key> =
  | {
      variant: 'listbox';
      selectionMode?: 'single' | 'multiple';
      items: TileListItem[];
      selectedKeys?: T[];
      onSelectionChange?: (selection: 'all' | Set<Key>) => void;
    }
  | {
      variant: 'actionButton';
      selectionMode?: never;
      items?: never;
      selectedKeys?: never;
      onSelectionChange?: never;
    };

export function TileList<T extends Key>(props: Props<T>) {
  const {
    testid,
    variant,
    className,
    ariaLabel,
    selectionMode,
    items,
    selectedKeys,
    onSelectionChange,
    children,
  } = props;

  return variant === 'listbox' ? (
    <ListBox
      data-testid={testid}
      aria-label={ariaLabel}
      selectionMode={selectionMode}
      items={items}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      className={clsx(styles.container, className)}
    >
      {children}
    </ListBox>
  ) : (
    <ul className={clsx(styles.container, className)}>{children}</ul>
  );
}
